import { restartableTask } from 'ember-concurrency';
import _merge from 'lodash/merge';
import { v4 as uuid } from 'uuid';
import DueTasksWidget from 'volta/components/widgets/due-tasks';
import ImportStatusWidget from 'volta/components/widgets/import-status';
import LastCommentsWidget from 'volta/components/widgets/last-comments';
import PlanningKpisWidget from 'volta/components/widgets/planning-kpis';
import ReportsWidget from 'volta/components/widgets/planning-report';
import TableauReportsWidget from 'volta/components/widgets/tableau-reports';
import { ResourceName as ImportResourceName } from 'volta/models/import-histo';
import { POResourceName } from 'volta/models/planning-order';
import { ReportResourceName } from 'volta/models/report';
import { SkuResourceName } from 'volta/models/sku';
import {
  IDashboardWidget,
  IDashboardWidgetRegistry,
  IDashboardWidgetWithComponent,
  IWidgetNames,
  TDashboardColumn
} from 'volta/models/types/user';
import AuthorizationService from 'volta/services/authorization';
import BvFlashService from 'volta/services/bv-flash';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import {
  getFilterValuesFromQueryParams,
  resetFilterValues,
  serializeFilterValues
} from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import PlanningOrdersWidget from '../widgets/planning-orders';

import type SkusCollectionDefinitionBuilder from 'volta/services/collection-builder-skus';
import type AppNavState from 'volta/services/app-nav-state';
import type SessionUserService from 'volta/services/session-user';

export const WidgetsRegistry: (
  auth: AuthorizationService
) => Partial<Record<IWidgetNames, IDashboardWidgetRegistry>> = (auth: AuthorizationService) => ({
  lastComments: {
    component: LastCommentsWidget,
    columnsDisplay: ['primary', 'secondary'],
    hidden: !(
      auth.can('ViewComments', POResourceName.plural) ||
      auth.can('ViewComments', SkuResourceName.plural)
    )
  },
  dueTasks: {
    component: DueTasksWidget,
    columnsDisplay: ['primary', 'secondary'],
    hidden: auth.cannot('ViewTasks', SkuResourceName.plural),
    args: {
      assignTo: undefined,
      // Giving types a value gives conflict when merging with user preferences
      types: []
    }
  },
  planningReport: {
    component: ReportsWidget,
    columnsDisplay: ['primary', 'secondary'],
    hidden: !auth.canView('plannings'),
    args: {
      avgStockValue: { disabled: false },
      stockouts: { disabled: false },
      planningBuffer: { disabled: false },
      executionBuffer: { disabled: false }
    }
  },
  importStatus: {
    component: ImportStatusWidget,
    columnsDisplay: ['primary', 'secondary'],
    hidden: !auth.canView(ImportResourceName.plural)
  },
  tableauReport: {
    component: TableauReportsWidget,
    columnsDisplay: ['primary', 'secondary'],
    hidden: !auth.canView(ReportResourceName.plural)
  },
  planningOrders: {
    component: PlanningOrdersWidget,
    columnsDisplay: ['secondary'],
    hidden: !auth.canView(POResourceName.plural),
    args: {
      collectionViewId: undefined,
      collectionViewName: undefined,
      planningBuffer: { disabled: false },
      executionBuffer: { disabled: false },
      stats: { disabled: false },
      statuses: { disabled: false }
    }
  },
  planningKpis: {
    component: PlanningKpisWidget,
    columnsDisplay: ['primary', 'secondary'],
    hidden: !auth.canView('plannings'),
    args: {
      planningBuffer: { disabled: false },
      executionBuffer: { disabled: false }
    }
  }
});

interface IArgs {
  queryParamsValues: TQueryParams;
}

export default class Dashboard extends Component<IArgs> {
  @service sessionUser!: SessionUserService;
  @service appNavState!: AppNavState;
  @service router!: RouterService;
  @service authorization!: AuthorizationService;
  @service bvFlash!: BvFlashService;
  @service collectionBuilderSkus!: SkusCollectionDefinitionBuilder;

  @tracked editMode = false;
  @tracked selectedWidget?: IDashboardWidget;
  @tracked addWidgetsModalOpened = false;

  @tracked sorts = [{ valuePath: 'dueDate', isAscending: true }];
  @tracked searchQuery = '';
  @tracked filterValues: { [key: string]: TFilterValue | undefined } = {};

  resourceName = SkuResourceName;

  @tracked widgets: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>;

  @cached
  get dashboardPreferences() {
    return this.sessionUser.preferences.dashboard;
  }

  constructor(owner: any, args: IArgs) {
    super(owner, args);

    const registry = WidgetsRegistry(this.authorization);
    const { dashboardPreferences } = this;

    this.widgets = dashboardPreferences
      .map((widget) => _merge({}, registry[widget.name], widget, { _id: uuid() }))
      .reduce(
        (
          acc: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>,
          widget: IDashboardWidgetWithComponent
        ) => {
          if (!widget.disabled) {
            acc[widget.column].push(widget);
            return acc;
          }
          return acc;
        },
        { primary: [], secondary: [] }
      );
  }

  get primary() {
    return this.widgets.primary;
  }

  get secondary() {
    return this.widgets.secondary;
  }

  get filterDefinitions() {
    return this.collectionBuilderSkus.buildFilters(this.args.queryParamsValues);
  }

  saveDashboardPreferences = restartableTask(async () => {
    try {
      const dashboard = this.parseWidgets();
      this.sessionUser.setDashboard(dashboard);
    } catch (e) {
      defaultErrorHandler(e);
      this.bvFlash.error(e);
      return;
    }
  });

  @action
  updateWidget(widget: IDashboardWidgetWithComponent, column: TDashboardColumn) {
    this.widgets[column] = this.widgets[column].reduce(
      (acc: IDashboardWidgetWithComponent[], w) => {
        if (w._id !== widget._id) {
          acc.push(w);
        } else {
          acc.push(widget);
        }
        return acc;
      },
      []
    );
    this.saveDashboardPreferences.perform();
  }

  @action
  updateWidgets(orderedWidgets: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>) {
    this.widgets = orderedWidgets;
    this.saveDashboardPreferences.perform();
  }

  @action
  onSearchChange(_filterValues: { [key: string]: TFilterValue }, query: string): void {
    this.searchQuery = query;
    const filterQuery: { [key: string]: TFilterValue | undefined } = {
      ...this.filterValues
    };
    if (isPresent(query)) {
      filterQuery.q = {
        comparator: 'lk',
        values: [query],
        enabled: true
      };
    } else {
      filterQuery.q = undefined;
    }
    const queryParams = serializeFilterValues(filterQuery, this.filterDefinitions);
    this.router.transitionTo('protected.dashboard', {
      queryParams
    });
  }

  @action
  initFilters() {
    this.filterValues = getFilterValuesFromQueryParams(
      this.args.queryParamsValues,
      this.filterDefinitions
    );
    this.searchQuery = this.filterValues.q?.values[0] || '';
  }

  @action
  handleClearFilters() {
    this.filterValues = resetFilterValues(this.filterDefinitions);
    this.router.transitionTo('protected.dashboard', {
      queryParams: serializeFilterValues(this.filterValues, this.filterDefinitions)
    });
  }

  @action
  onFilterChange(filters: { [key: string]: TFilterValue }) {
    this.filterValues = filters;
    const queryParams = serializeFilterValues(this.filterValues, this.filterDefinitions);
    queryParams.customFields = queryParams.customFields
      ? JSON.stringify(queryParams.customFields)
      : undefined;
    this.router.transitionTo('protected.dashboard', {
      queryParams
    });
  }

  private parseWidgets() {
    const { primary, secondary } = this.widgets;

    const flatWidgets = primary.concat(secondary);
    return flatWidgets.map((widget) => {
      const { component, columnsDisplay, _id, ...propsToKeep } = widget;
      return propsToKeep;
    });
  }
}
